import React from "react";
import { Image, View, TouchableOpacity } from "react-native";
import { StyleSheet } from "@/utils/appStyleSheet";
import { AppText } from "@/components/AppText";
import { BASE_URL } from "@/constants/Configs";
import { FontFamilies } from "@/constants/Typography";
import Images from "@/assets/images";
import { OfferCardTag, OfferCardProps } from "./types";
import { Colors } from "@/constants/Colors";

interface OfferCardHeaderProps {
  data: OfferCardProps["data"];
  title: string;
  tags: OfferCardTag[];
  amountText?: string;
}

export function OfferCardHeader({
  data,
  title,
  tags,
  amountText,
}: OfferCardHeaderProps) {
  const renderImage = () => {
    if (data.type === "unclaimedMoney" || data.type === "unclaimedProperty") {
      return (
        <View style={styles.imageContainer}>
          <Image
            source={Images.unclaimedTemplate}
            style={styles.image}
            resizeMode="cover"
          />
        </View>
      );
    }

    if (Boolean(data.image)) {
      return (
        <View style={styles.imageContainer}>
          <Image
            source={{ uri: BASE_URL + data.image }}
            style={styles.image}
            resizeMode="cover"
          />
        </View>
      );
    }

    return null;
  };

  return (
    <View style={styles.header}>
      {renderImage()}
      <View style={styles.leftContent}>
        {/* Description */}
        <AppText style={styles.description} numberOfLines={2}>
          {title}
        </AppText>
        <AppText style={styles.amountText} numberOfLines={2}>
          {amountText}
        </AppText>
      </View>
      {/* Tags */}
      <View style={styles.tagsContainer}>
        {(tags ?? []).map((tag) => {
          const TagWrapper = tag.onPress ? TouchableOpacity : View;
          return (
            <TagWrapper
              key={tag.text}
              onPress={tag.onPress}
              style={[styles.tag, { backgroundColor: tag.backgroundColor }]}
            >
              {tag.icon}
              <AppText style={[styles.tagText, { color: tag.color }]}>
                {tag.text}
              </AppText>
            </TagWrapper>
          );
        })}
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  header: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    borderBottomWidth: 1,
    borderColor: "#F3F3F3",
    padding: 16,
    gap: 16,
    overflow: "hidden",
  },
  leftContent: {
    flex: 1,
    flexShrink: 1,
    gap: 4,
  },
  tagsContainer: {
    flexDirection: "row",
    alignSelf: "center",
    flexWrap: "wrap",
    gap: 8,
    flexShrink: 0,
    maxWidth: 100,
  },
  tag: {
    flexDirection: "row",
    gap: 4,
    paddingHorizontal: 8,
    borderRadius: 12,
    alignSelf: "flex-start",
    justifyContent: "center",
    alignItems: "center",
    backgroundColor: "#F4F5F6",
  },
  tagText: {
    fontFamily: FontFamilies.Inter.Medium,
    fontSize: 10,
    color: "#161D14",
    textAlign: "center",
  },
  amountText: {
    fontFamily: FontFamilies.InterTight.SemiBold,
    fontSize: 20,
    color: Colors.text,
  },
  description: {
    fontFamily: FontFamilies.Inter.Regular,
    fontSize: 15,
    color: "#161D14",
  },
  imageContainer: {
    width: 80,
    height: 80,
    borderRadius: 16,
    justifyContent: "center",
    alignItems: "center",
    backgroundColor: "#F7F8FA",
  },
  image: {
    width: "100%",
    height: "100%",
    borderWidth: 1.6,
    borderColor: Colors.lightGrey2,
    borderRadius: 16,
  },
});
